// Create Series Dialog Component
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useSeries } from '../../hooks/useSeries';
import { useLenders } from '../../hooks/useLenders';

interface CreateSeriesDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateSeriesDialog: React.FC<CreateSeriesDialogProps> = ({ open, onClose, onSuccess }) => {
  const { addSeries } = useSeries();
  const { lenders, loading: loadingLenders } = useLenders();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    lenderId: '',
    principalAmount: '',
    closingDate: new Date().toISOString().split('T')[0],
  });

  const handleChange = (field: string) => (e: any) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.lenderId) {
      setError('Please select a lender');
      return false;
    }
    const amount = parseFloat(formData.principalAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Principal amount must be greater than 0');
      return false;
    }
    if (!formData.closingDate) {
      setError('Closing date is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const principal = parseFloat(formData.principalAmount);
      const closingDate = new Date(formData.closingDate);
      const issueDate = new Date(formData.closingDate);
      
      // Calculate lock-up end date (12 months from issue date)
      const lockupEnd = new Date(issueDate);
      lockupEnd.setMonth(lockupEnd.getMonth() + 12);

      await addSeries({
        lenderId: formData.lenderId,
        principalAmount: principal,
        closingDate: closingDate,
        issueDate: issueDate,
        lockupEnd: lockupEnd,
        highWaterMark: principal, // Initial HWM = principal
        status: 'active',
        currentNAV: principal, // Initial NAV = principal
        capitalizedInterest: 0, // Initialize capitalized interest
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      // Reset form
      setFormData({
        lenderId: '',
        principalAmount: '',
        closingDate: new Date().toISOString().split('T')[0],
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create series');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  const selectedLender = lenders.find(l => l.id === formData.lenderId);
  const principal = parseFloat(formData.principalAmount);
  const closingDate = new Date(formData.closingDate);
  const lockupEndDate = new Date(closingDate);
  lockupEndDate.setMonth(lockupEndDate.getMonth() + 12);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          border: '1px solid #FFD700',
        }
      }}
    >
      <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #333' }}>
        Create New Series
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#999' }}>Select Lender *</InputLabel>
            <Select
              value={formData.lenderId}
              onChange={handleChange('lenderId')}
              label="Select Lender *"
              disabled={loading || loadingLenders}
              sx={{ color: 'white' }}
            >
              {lenders.length === 0 && (
                <MenuItem value="">
                  <em>No lenders available</em>
                </MenuItem>
              )}
              {lenders.map((lender) => (
                <MenuItem key={lender.id} value={lender.id}>
                  {lender.fullName} - {lender.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedLender && (
            <Box sx={{ p: 2, bgcolor: '#0a0a0a', borderRadius: 1, border: '1px solid #333' }}>
              <Typography variant="caption" sx={{ color: '#999' }}>
                Selected Lender Details
              </Typography>
              <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
                <strong>Name:</strong> {selectedLender.fullName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                <strong>Email:</strong> {selectedLender.email}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                <strong>Phone:</strong> {selectedLender.phone}
              </Typography>
            </Box>
          )}

          <TextField
            required
            fullWidth
            label="Principal Amount (USD)"
            type="number"
            value={formData.principalAmount}
            onChange={handleChange('principalAmount')}
            disabled={loading}
            placeholder="1000000"
            InputLabelProps={{ style: { color: '#999' } }}
            InputProps={{ 
              style: { color: 'white' },
              inputProps: { min: 0, step: 1000 }
            }}
          />

          <TextField
            required
            fullWidth
            label="Closing Date"
            type="date"
            value={formData.closingDate}
            onChange={handleChange('closingDate')}
            disabled={loading}
            InputLabelProps={{ 
              shrink: true,
              style: { color: '#999' }
            }}
            InputProps={{ style: { color: 'white' } }}
          />

          {formData.principalAmount && !isNaN(principal) && principal > 0 && (
            <Box sx={{ p: 2, bgcolor: '#1a1a1a', borderRadius: 1, border: '1px solid #FFD700' }}>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
                Series Summary
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                <strong>Principal:</strong> ${principal.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                <strong>Initial NAV:</strong> ${principal.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                <strong>High Water Mark:</strong> ${principal.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                <strong>Closing Date:</strong> {closingDate.toLocaleDateString()}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                <strong>Lock-up End:</strong> {lockupEndDate.toLocaleDateString()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFD700', mt: 1 }}>
                <strong>Lock-up Period:</strong> 12 months
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ color: 'white' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading || lenders.length === 0}
          variant="contained"
          sx={{
            bgcolor: '#FFD700',
            color: '#000',
            '&:hover': { bgcolor: '#FFC700' },
          }}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Series'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSeriesDialog;
